import React from 'react';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useLanguage } from '../../i18n';

const Step1_PLZCheck = ({ 
  formData,
  plzCheckStatus,
  plzCheckData,
  plzMultipleOptions,
  showWaitlistForm,
  waitlistData,
  loading,
  onInputChange,
  onWaitlistInputChange,
  onMultipleOptionSelect,
  onShowWaitlistForm,
  onSubmitWaitlist,
  onProceedToStep2,
  onBackToHome
}) => {
  const { t } = useLanguage();

  return (
    <div className="step-content" data-testid="step-1">
      <h2 className="step-title">{t('step1Title')}</h2>
      <p className="step-description">{t('step1Desc')}</p>
      <div className="form-group">
        <label className="form-label">{t('plzLabel')}</label>
        <input
          type="text"
          name="plz"
          value={formData.plz}
          onChange={onInputChange}
          className="form-input"
          placeholder={t('plzPlaceholder')}
          data-testid="plz-input"
        />
        
        {plzCheckStatus === 'checking' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.75rem', color: '#666' }}>
            <Loader2 size={18} className="animate-spin" style={{ animation: 'spin 1s linear infinite' }} />
            <span style={{ fontSize: '0.875rem' }}>{t('plzChecking')}</span>
          </div>
        )}
        
        {plzCheckStatus === 'multiple_choice' && plzMultipleOptions.length > 0 && (
          <div style={{ marginTop: '0.75rem', padding: '1rem', background: '#E3F2FD', borderRadius: '8px', border: '1px solid #2196F3' }}>
            <p style={{ margin: '0 0 1rem 0', fontWeight: 600, color: '#1976D2', fontSize: '0.875rem' }}>
              {t('plzMultipleFound')}
            </p>
            <div style={{ display: 'grid', gap: '0.5rem' }}>
              {plzMultipleOptions.map((option, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => onMultipleOptionSelect(option)}
                  className="btn-secondary"
                  style={{ 
                    justifyContent: 'flex-start', 
                    padding: '0.75rem',
                    background: option.available ? '#E8F5E9' : '#FFF3E0',
                    borderColor: option.available ? '#4CAF50' : '#F57C00'
                  }}
                >
                  <div>
                    <strong>{formData.plz} – {option.location_name} ({option.country})</strong>
                    <div style={{ fontSize: '0.75rem', opacity: 0.8 }}>
                      {option.available ? `✓ ${t('plzAvailable')}` : `✗ ${t('plzNotAvailable')}`}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
        
        {plzCheckStatus === 'multiple_choice' && plzMultipleOptions.length > 0 && 
         plzMultipleOptions.every(opt => !opt.available) && (
          <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#FFEBEE', borderRadius: '8px', border: '1px solid #EF5350' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <XCircle size={20} style={{ color: '#C62828', flexShrink: 0 }} />
              <p style={{ margin: 0, fontWeight: 600, color: '#C62828', fontSize: '0.875rem' }}>
                {t('plzCurrentlyUnavailable')}
              </p>
            </div>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.875rem', color: '#666' }}>
              {t('plzAllUnavailableText')}
            </p>
            <button 
              onClick={() => onShowWaitlistForm(true)} 
              className="btn-primary" 
              style={{ fontSize: '0.875rem' }}
            >
              {t('plzJoinWaitlist')}
            </button>
          </div>
        )}
        
        {plzCheckStatus === 'available' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.75rem', padding: '0.75rem', background: '#E8F5E9', borderRadius: '8px', border: '1px solid #4CAF50' }} data-testid="plz-available-feedback">
            <CheckCircle size={20} style={{ color: '#2E7D32', flexShrink: 0 }} />
            <div>
              <p style={{ margin: 0, fontWeight: 600, color: '#2E7D32', fontSize: '0.875rem' }}>
                {t('plzRegionAvailable')}
              </p>
              {plzCheckData?.location_name && (
                <p style={{ margin: 0, fontSize: '0.75rem', color: '#666' }}>{plzCheckData.location_name}</p>
              )}
            </div>
          </div>
        )}
        
        {plzCheckStatus === 'unavailable' && (
          <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#FFEBEE', borderRadius: '8px', border: '1px solid #EF5350' }} data-testid="plz-unavailable-feedback">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <XCircle size={20} style={{ color: '#C62828', flexShrink: 0 }} />
              <p style={{ margin: 0, fontWeight: 600, color: '#C62828', fontSize: '0.875rem' }}>
                {t('plzCurrentlyUnavailable')}
              </p>
            </div>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.875rem', color: '#666' }}>
              {t('plzUnavailableText')}
            </p>
            
            {showWaitlistForm && (
              <div style={{ background: 'white', padding: '1rem', borderRadius: '8px', marginTop: '0.75rem' }} data-testid="waitlist-form">
                <div className="form-group" style={{ marginBottom: '0.75rem' }}>
                  <label className="form-label" style={{ fontSize: '0.875rem' }}>{t('waitlistName')}</label>
                  <input
                    type="text"
                    name="name"
                    value={waitlistData.name}
                    onChange={onWaitlistInputChange}
                    className="form-input"
                    placeholder={t('waitlistNamePlaceholder')}
                    data-testid="waitlist-name-input"
                  />
                </div>
                <div className="form-group" style={{ marginBottom: '0.75rem' }}>
                  <label className="form-label" style={{ fontSize: '0.875rem' }}>{t('waitlistEmail')}</label>
                  <input
                    type="email"
                    name="email"
                    value={waitlistData.email}
                    onChange={onWaitlistInputChange}
                    className="form-input"
                    placeholder={t('waitlistEmailPlaceholder')}
                    data-testid="waitlist-email-input"
                  />
                </div>
                <div className="checkbox-group" style={{ marginBottom: '1rem' }}>
                  <input
                    type="checkbox"
                    name="consent"
                    checked={waitlistData.consent}
                    onChange={onWaitlistInputChange}
                    id="waitlist-consent"
                    data-testid="waitlist-consent-checkbox"
                  />
                  <label htmlFor="waitlist-consent" style={{ fontSize: '0.75rem' }}>
                    {t('waitlistConsent')} <a href="/datenschutz" target="_blank" style={{ color: '#1B5E20', textDecoration: 'underline' }}>{t('waitlistPrivacyLink')}</a>*
                  </label>
                </div>
                <button 
                  onClick={onSubmitWaitlist} 
                  className="btn-primary" 
                  style={{ width: '100%', fontSize: '0.875rem' }}
                  disabled={loading || !waitlistData.name || !waitlistData.email || !waitlistData.consent}
                  data-testid="waitlist-submit-btn"
                >
                  {loading ? t('sending') : t('waitlistSubmit')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      
      {plzCheckStatus === 'available' && (
        <div className="button-group">
          <button onClick={onBackToHome} className="btn-secondary" data-testid="back-to-home-btn">
            {t('back')}
          </button>
          <button onClick={onProceedToStep2} className="btn-primary" data-testid="proceed-to-step-2-btn">
            {t('plzProceed')}
          </button>
        </div>
      )}
      
      {!plzCheckStatus && (
        <div className="button-group">
          <button onClick={onBackToHome} className="btn-secondary" data-testid="back-to-home-btn">
            {t('back')}
          </button>
        </div>
      )}
    </div>
  );
};

export default Step1_PLZCheck;
